import {
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

export type SharedPopoverSide = 'top' | 'bottom' | 'left' | 'right' | 'start' | 'end';
export type SharedPopoverAlignment = 'start' | 'center' | 'end';
export type SharedPopoverTriggerAction = 'click';

const POPOVER_ANIMATION_MS = 200;
const VIEWPORT_MARGIN_PX = 8;
const ANCHOR_GAP_PX = 4;

/**
 * Non-Ionic replacement for ion-popover.
 * Supports trigger-by-id (declarative) and controlled/programmatic present() modes.
 *
 * Trigger-based instances register in a shared Map and share one document click/Escape
 * listener. Programmatic instances (no trigger) only participate in openInstances.
 */
@Component({
  selector: 'app-shared-popover',
  templateUrl: './shared-popover.component.html',
  styleUrls: ['./shared-popover.component.scss'],
  standalone: false,
})
export class SharedPopoverComponent implements OnInit, OnChanges, OnDestroy {
  @Input() trigger?: string;
  @Input() triggerAction: SharedPopoverTriggerAction = 'click';
  @Input() isOpen: boolean | undefined;
  @Input() showBackdrop = true;
  @Input() backdropDismiss = true;
  @Input() dismissOnSelect = false;
  @Input() side: SharedPopoverSide = 'bottom';
  @Input() alignment: SharedPopoverAlignment = 'start';
  @Input() event: MouseEvent | PointerEvent | null = null;
  @Input() cssClass: string | string[] = '';
  @Input() ariaLabel = 'Popover';

  @Output() readonly isOpenChange = new EventEmitter<boolean>();
  @Output() readonly didDismiss = new EventEmitter<void>();
  @Output() readonly didPresent = new EventEmitter<void>();

  @ViewChild('backdropEl') private backdropEl?: ElementRef<HTMLElement>;
  @ViewChild('containerEl') private containerEl?: ElementRef<HTMLElement>;
  @ContentChildren(SharedPopoverComponent, {descendants: true})
  private childPopovers!: QueryList<SharedPopoverComponent>;

  isRendered = false;
  isVisible = false;
  popoverTop = 0;
  popoverLeft = 0;

  private closeTimeout: ReturnType<typeof setTimeout> | null = null;
  private attachedToBody = false;
  private isDismissing = false;
  private registeredTriggerId: string | null = null;
  private lastAnchor: DOMRect | null = null;

  private static readonly openInstances: SharedPopoverComponent[] = [];
  private static readonly triggerRegistry = new Map<string, SharedPopoverComponent>();
  private static documentListenersAttached = false;
  private static repositionListenersAttached = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly hostRef: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
  ) {
  }

  get cssClassList(): string[] {
    if (!this.cssClass) {
      return [];
    }
    return Array.isArray(this.cssClass) ? this.cssClass : [this.cssClass];
  }

  private static readonly onSharedDocumentClick = (event: MouseEvent): void => {
    SharedPopoverComponent.handleSharedDocumentClick(event);
  };

  private static readonly onSharedDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') {
      return;
    }
    const openWithBackdropDismiss = SharedPopoverComponent.openInstances.filter(
      (popover: SharedPopoverComponent) => popover.isRendered && popover.backdropDismiss,
    );
    if (openWithBackdropDismiss.length === 0) {
      return;
    }
    event.preventDefault();
    SharedPopoverComponent.dismissAll();
  };

  private static readonly onSharedReposition = (): void => {
    SharedPopoverComponent.openInstances.forEach((popover: SharedPopoverComponent) => {
      if (popover.isRendered) {
        popover.reposition();
      }
    });
  };



  /**
   * Closes every open shared popover (parent + nested).
   */
  static dismissAll(): void {
    [...SharedPopoverComponent.openInstances].forEach((popover: SharedPopoverComponent) => {
      popover.dismiss();
    });
  }

  ngOnInit(): void {
    this.syncTriggerRegistration();
    if (this.isOpen === true) {
      this.present(this.event ?? undefined);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['trigger']) {
      this.syncTriggerRegistration();
    }
    if (changes['isOpen'] && !changes['isOpen'].firstChange) {
      if (this.isOpen === true) {
        this.present(this.event ?? undefined);
      } else if (this.isOpen === false) {
        this.dismiss();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
    if (this.isRendered) {
      this.isRendered = false;
      this.isVisible = false;
      this.removeFromOpenInstances();
      this.detachFromBody();
    }
    this.unregisterTrigger();
  }

  /**
   * Opens the popover, optionally positioned from a pointer event.
   */
  present(fromEvent?: Event): void {
    this.ngZone.run(() => {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
      this.isDismissing = false;
      this.updatePosition(fromEvent ?? this.event ?? undefined);
      this.isRendered = true;
      this.addToOpenInstances();
      this.isOpenChange.emit(true);
      this.cdr.detectChanges();
      this.attachToBody();
      this.clampAndFlipToViewport();
      this.cdr.detectChanges();
      requestAnimationFrame(() => {
        this.clampAndFlipToViewport();
        this.isVisible = true;
        this.cdr.markForCheck();
        this.didPresent.emit();
      });
    });
  }

  /**
   * Closes the popover with the leave animation.
   */
  dismiss(): void {
    if (!this.isRendered || this.isDismissing) {
      return;
    }
    this.isDismissing = true;
    this.ngZone.run(() => {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
      this.childPopovers?.forEach((child: SharedPopoverComponent) => {
        if (child !== this) {
          child.dismiss();
        }
      });
      this.isVisible = false;
      this.cdr.markForCheck();
      this.closeTimeout = setTimeout(() => {
        this.isRendered = false;
        this.isDismissing = false;
        this.closeTimeout = null;
        this.removeFromOpenInstances();
        this.detachFromBody();
        this.isOpenChange.emit(false);
        this.didDismiss.emit();
        this.cdr.markForCheck();
      }, POPOVER_ANIMATION_MS);
    });
  }

  onBackdropClick(): void {
    if (this.backdropDismiss) {
      this.dismiss();
    }
  }

  onContentClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-shared-popover-keep-open]')) {
      return;
    }
    if (this.dismissOnSelect) {
      SharedPopoverComponent.dismissAll();
      return;
    }
    event.stopPropagation();
  }

  private reposition(): void {
    this.ngZone.run(() => {
      this.updatePosition();
      this.clampAndFlipToViewport();
      this.cdr.markForCheck();
    });
  }

  private syncTriggerRegistration(): void {
    const nextTrigger = this.trigger?.trim() ? this.trigger : null;
    if (this.registeredTriggerId === nextTrigger) {
      return;
    }
    this.unregisterTrigger();
    if (nextTrigger) {
      this.registeredTriggerId = nextTrigger;
      SharedPopoverComponent.triggerRegistry.set(nextTrigger, this);
      SharedPopoverComponent.ensureDocumentListeners(this.ngZone);
    }
  }

  private unregisterTrigger(): void {
    if (!this.registeredTriggerId) {
      return;
    }
    if (SharedPopoverComponent.triggerRegistry.get(this.registeredTriggerId) === this) {
      SharedPopoverComponent.triggerRegistry.delete(this.registeredTriggerId);
    }
    this.registeredTriggerId = null;
    SharedPopoverComponent.teardownDocumentListenersIfIdle();
  }

  private addToOpenInstances(): void {
    if (!SharedPopoverComponent.openInstances.includes(this)) {
      SharedPopoverComponent.openInstances.push(this);
    }
    SharedPopoverComponent.ensureRepositionListeners(this.ngZone);
  }

  private removeFromOpenInstances(): void {
    const index = SharedPopoverComponent.openInstances.indexOf(this);
    if (index > -1) {
      SharedPopoverComponent.openInstances.splice(index, 1);
    }
    SharedPopoverComponent.teardownRepositionListenersIfIdle();
  }

  private attachToBody(): void {
    if (this.attachedToBody) {
      return;
    }
    const backdrop = this.backdropEl?.nativeElement;
    const container = this.containerEl?.nativeElement;
    if (backdrop) {
      document.body.appendChild(backdrop);
    }
    if (container) {
      document.body.appendChild(container);
    }
    this.attachedToBody = true;
  }

  private detachFromBody(): void {
    if (!this.attachedToBody) {
      return;
    }
    const host = this.hostRef.nativeElement;
    const backdrop = this.backdropEl?.nativeElement;
    const container = this.containerEl?.nativeElement;
    if (backdrop && backdrop.parentElement === document.body) {
      host.appendChild(backdrop);
    }
    if (container && container.parentElement === document.body) {
      host.appendChild(container);
    }
    this.attachedToBody = false;
  }

  private updatePosition(fromEvent?: Event | MouseEvent | PointerEvent | null): void {
    const anchor = this.resolveAnchor(fromEvent);
    this.lastAnchor = anchor;

    if (!anchor) {
      if (fromEvent && 'clientX' in fromEvent) {
        const mouseEvent = fromEvent as MouseEvent;
        this.popoverTop = mouseEvent.clientY + ANCHOR_GAP_PX;
        this.popoverLeft = mouseEvent.clientX + ANCHOR_GAP_PX;
        return;
      }
      this.popoverTop = window.innerHeight / 2;
      this.popoverLeft = window.innerWidth / 2;
      return;
    }

    switch (this.side) {
      case 'end':
      case 'right':
        this.popoverTop = this.alignVertical(anchor, this.alignment);
        this.popoverLeft = anchor.right + ANCHOR_GAP_PX;
        break;
      case 'start':
      case 'left':
        this.popoverTop = this.alignVertical(anchor, this.alignment);
        this.popoverLeft = anchor.left - ANCHOR_GAP_PX;
        break;
      case 'top':
        this.popoverTop = anchor.top - ANCHOR_GAP_PX;
        this.popoverLeft = this.alignHorizontal(anchor, this.alignment);
        break;
      case 'bottom':
      default:
        this.popoverTop = anchor.bottom + ANCHOR_GAP_PX;
        this.popoverLeft = this.alignHorizontal(anchor, this.alignment);
        break;
    }
  }

  private resolveAnchor(fromEvent?: Event | MouseEvent | PointerEvent | null): DOMRect | null {
    if (this.trigger) {
      const el = document.getElementById(this.trigger);
      if (el) {
        return el.getBoundingClientRect();
      }
    }

    if (this.lastAnchor && !fromEvent) {
      if (this.trigger) {
        const el = document.getElementById(this.trigger);
        if (el) {
          return el.getBoundingClientRect();
        }
      }
      return this.lastAnchor;
    }

    if (fromEvent?.currentTarget instanceof HTMLElement) {
      return fromEvent.currentTarget.getBoundingClientRect();
    }

    if (fromEvent?.target instanceof HTMLElement) {
      return fromEvent.target.getBoundingClientRect();
    }

    return this.lastAnchor;
  }

  private clampAndFlipToViewport(): void {
    const container = this.containerEl?.nativeElement;
    if (!container) {
      return;
    }

    const width = container.offsetWidth;
    const height = container.offsetHeight;
    if (width <= 0 || height <= 0) {
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const anchor = this.lastAnchor;
    let top = this.popoverTop;
    let left = this.popoverLeft;

    if (anchor) {
      const side = this.side;

      if ((side === 'end' || side === 'right') && anchor.right + ANCHOR_GAP_PX + width > viewportWidth - VIEWPORT_MARGIN_PX) {
        left = anchor.left - ANCHOR_GAP_PX - width;
      } else if ((side === 'start' || side === 'left') && anchor.left - ANCHOR_GAP_PX - width < VIEWPORT_MARGIN_PX) {
        left = anchor.right + ANCHOR_GAP_PX;
      } else if (side === 'start' || side === 'left') {
        left = anchor.left - ANCHOR_GAP_PX - width;
      }

      if ((side === 'bottom') && anchor.bottom + ANCHOR_GAP_PX + height > viewportHeight - VIEWPORT_MARGIN_PX) {
        top = anchor.top - ANCHOR_GAP_PX - height;
      } else if (side === 'top' && anchor.top - ANCHOR_GAP_PX - height < VIEWPORT_MARGIN_PX) {
        top = anchor.bottom + ANCHOR_GAP_PX;
      } else if (side === 'top') {
        top = anchor.top - ANCHOR_GAP_PX - height;
      }

      if (side === 'bottom' || side === 'top') {
        if (this.alignment === 'end') {
          left = anchor.right - width;
        } else if (this.alignment === 'center') {
          left = anchor.left + anchor.width / 2 - width / 2;
        }
      }
    }

    left = Math.min(Math.max(VIEWPORT_MARGIN_PX, left), Math.max(VIEWPORT_MARGIN_PX, viewportWidth - VIEWPORT_MARGIN_PX - width));
    top = Math.min(Math.max(VIEWPORT_MARGIN_PX, top), Math.max(VIEWPORT_MARGIN_PX, viewportHeight - VIEWPORT_MARGIN_PX - height));

    this.popoverTop = top;
    this.popoverLeft = left;
  }

  private alignHorizontal(anchor: DOMRect, alignment: SharedPopoverAlignment): number {
    switch (alignment) {
      case 'center':
        return anchor.left + anchor.width / 2;
      case 'end':
        return anchor.right;
      case 'start':
      default:
        return anchor.left;
    }
  }

  private alignVertical(anchor: DOMRect, alignment: SharedPopoverAlignment): number {
    switch (alignment) {
      case 'center':
        return anchor.top + anchor.height / 2;
      case 'end':
        return anchor.bottom;
      case 'start':
      default:
        return anchor.top;
    }
  }

  private static handleSharedDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target) {
      return;
    }

    const triggerPopover = SharedPopoverComponent.findTriggerPopover(target);
    if (triggerPopover && triggerPopover.triggerAction === 'click') {
      event.preventDefault();
      event.stopPropagation();
      if (triggerPopover.isRendered) {
        triggerPopover.dismiss();
      } else {
        triggerPopover.present(event);
      }
      return;
    }

    if (SharedPopoverComponent.isEventInsideOpenPopover(target)) {
      return;
    }

    if (SharedPopoverComponent.openInstances.length === 0) {
      return;
    }

    const shouldDismiss = SharedPopoverComponent.openInstances.some(
      (popover: SharedPopoverComponent) => popover.backdropDismiss,
    );
    if (shouldDismiss) {
      SharedPopoverComponent.dismissAll();
    }
  }

  private static findTriggerPopover(target: Node): SharedPopoverComponent | undefined {
    let node: HTMLElement | null = target instanceof HTMLElement ? target : target.parentElement;
    while (node) {
      if (node.id && SharedPopoverComponent.triggerRegistry.has(node.id)) {
        return SharedPopoverComponent.triggerRegistry.get(node.id);
      }
      node = node.parentElement;
    }
    return undefined;
  }

  private static isEventInsideOpenPopover(target: Node): boolean {
    return SharedPopoverComponent.openInstances.some((popover: SharedPopoverComponent) => {
      const container = popover.containerEl?.nativeElement;
      const backdrop = popover.backdropEl?.nativeElement;
      return Boolean(
        (container && (container === target || container.contains(target))) ||
        (backdrop && (backdrop === target || backdrop.contains(target))),
      );
    });
  }

  private static ensureDocumentListeners(ngZone: NgZone): void {
    if (SharedPopoverComponent.documentListenersAttached) {
      return;
    }
    ngZone.runOutsideAngular(() => {
      document.addEventListener('click', SharedPopoverComponent.onSharedDocumentClick, true);
      document.addEventListener('keydown', SharedPopoverComponent.onSharedDocumentKeydown, true);
    });
    SharedPopoverComponent.documentListenersAttached = true;
  }

  private static teardownDocumentListenersIfIdle(): void {
    if (SharedPopoverComponent.triggerRegistry.size > 0 || !SharedPopoverComponent.documentListenersAttached) {
      return;
    }
    document.removeEventListener('click', SharedPopoverComponent.onSharedDocumentClick, true);
    document.removeEventListener('keydown', SharedPopoverComponent.onSharedDocumentKeydown, true);
    SharedPopoverComponent.documentListenersAttached = false;
  }

  private static ensureRepositionListeners(ngZone: NgZone): void {
    if (SharedPopoverComponent.repositionListenersAttached) {
      return;
    }
    ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', SharedPopoverComponent.onSharedReposition);
      window.addEventListener('scroll', SharedPopoverComponent.onSharedReposition, true);
    });
    SharedPopoverComponent.repositionListenersAttached = true;
  }

  private static teardownRepositionListenersIfIdle(): void {
    if (SharedPopoverComponent.openInstances.length > 0 || !SharedPopoverComponent.repositionListenersAttached) {
      return;
    }
    window.removeEventListener('resize', SharedPopoverComponent.onSharedReposition);
    window.removeEventListener('scroll', SharedPopoverComponent.onSharedReposition, true);
    SharedPopoverComponent.repositionListenersAttached = false;
  }
}
